import { useState, useEffect, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { Typography } from '@components/Typography';
import { TagChip } from '@components/TagChip';
import Avatar, { RemovedPersonAvatar } from '@components/Avatar';
import { Checkbox } from '@components/Checkbox';
import { getInitialsParts, getFullName, getInitialsFull, getUserContacts } from '@helpers/userName';
import { getConversationChannelIcon } from '@helpers/channels';
import { getHumanizedDate } from '@helpers/dates';
import { MessageContentType, MessageNoticeKey, MessageStatus, MessageType } from '@constants/inbox';
import { useAccountPeople, useTranslation } from '@hooks';
import { UserIcon, PulseAiIcon } from 'assets/images/icons';
import { CHANNEL } from '@constants/channels';

const StyledUserIcon = styled(UserIcon)({
    width: '18px !important',
    height: '18px !important',
    '& path': {
        fill: 'var(--text-tertiary)',
        fillOpacity: 1,
    },
});

const StyledAvatar = styled(Avatar)({
    border: 'none',
});

const DialogWrapper = styled('div', {
    shouldForwardProp: prop => prop !== 'active' && prop !== 'read',
})(({ active, read }) => ({
    willChange: 'scroll-position',
    border: active ? '1px solid #1F4C5C' : '1px solid rgba(0, 0, 0, 0.12)',
    backgroundColor: active || !read ? 'var(--secondary-light)' : '#fff',
    padding: 8,
    borderRadius: 8,
    cursor: 'pointer',
    marginBottom: 8,
    '&:has(.AvatarSelectCheckbox:hover)': {
        borderColor: 'var(--primary-main)',
    },
}));

const AvatarSelectWrapper = styled('div', {
    shouldForwardProp: prop => prop !== 'allSelected',
})(({ allSelected }) => ({
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: '#transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
        backgroundColor: allSelected ? 'transparent' : 'var(--action-hover)',
    },
    '& .MuiSvgIcon-root': {
        fontSize: '1.125rem',
    },
    '& .MuiCheckbox-root': {
        padding: allSelected ? 0 : '11px',
    },
}));

const Dialog = ({ data, isActive, onSetActive, isSelected, allSelected, onSelect, language, isMobile }) => {
    const [isHover, setIsHover] = useState(false);
    const [, setTime] = useState(new Date());

    const { t } = useTranslation();

    useEffect(() => {
        const interval = setInterval(() => setTime(new Date()), 30000);
        return () => clearInterval(interval);
    }, []);

    const lastMessage = {
        [MessageContentType.Text]: data.lastMessage,
        [MessageContentType.Image]: t('common.image'),
        [MessageContentType.File]: t('common.file'),
        [MessageContentType.Video]: t('common.video'),
        [MessageContentType.Audio]: t('common.audio'),
        [MessageContentType.StoryImage]: t('common.story'),
        [MessageContentType.StoryVideo]: t('common.story'),
        [MessageContentType.Html]: data.lastMessage,
        [MessageContentType.Unsupported]: t(MessageNoticeKey[MessageContentType.Unsupported]),
        [MessageContentType.MediaUnavailable]: t(MessageNoticeKey[MessageContentType.MediaUnavailable]),
    };

    const channelName = data?.channelId === CHANNEL.ARCHIVED ? t('common.archived') : data?.channelName;

    const { getPerson } = useAccountPeople();
    const assignedUser = getPerson(data.assignedUserId);

    const userContacts = useMemo(() => {
        return getUserContacts(
            {
                firstName: data.firstName,
                lastName: data.lastName,
                phoneNumber: data.phoneNumber,
                email: data.email,
                externalId: data.contactExternalId,
            },
            '-',
        );
    }, [data]);

    return (
        <DialogWrapper
            active={isActive}
            onClick={onSetActive}
            read={data.read}
            onMouseOver={() => setIsHover(true)}
            onMouseLeave={() => setIsHover(false)}
        >
            <div style={{ display: 'grid', gridGap: '0 8px', gridTemplateColumns: '40px calc(100% - 48px)' }}>
                {(isHover && !isMobile) || isSelected ? (
                    <AvatarSelectWrapper onClick={e => e.stopPropagation()} allSelected={allSelected}>
                        <Checkbox
                            className="AvatarSelectCheckbox"
                            checked={isSelected}
                            onChange={onSelect}
                            disabled={allSelected}
                        />
                    </AvatarSelectWrapper>
                ) : (
                    <Avatar
                        size="large"
                        fullName={getFullName(data?.firstName, data?.lastName)}
                        label={getInitialsParts(data?.firstName, data?.lastName)}
                        icon={<StyledUserIcon />}
                        withBadge
                        badge={getConversationChannelIcon(data)}
                        onClick={
                            isMobile
                                ? e => {
                                      e.stopPropagation();
                                      onSelect();
                                  }
                                : null
                        }
                    />
                )}
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'var(--text-secondary)',
                                marginBottom: '2px',
                                display: 'block',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {channelName}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'var(--text-secondary)',
                                textAlign: 'end',
                                whiteSpace: 'nowrap',
                                '&:first-letter': {
                                    textTransform: 'capitalize',
                                },
                            }}
                        >
                            {getHumanizedDate(data.lastActivityAt, language)}
                        </Typography>
                    </div>
                    <Typography
                        variant="subtitle2"
                        sx={{ marginBottom: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                        {userContacts}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'var(--text-secondary)',
                            display: 'block',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontStyle:
                                data.lastMessageStatus === MessageStatus.Deleted ? 'italic !important' : 'normal',
                        }}
                    >
                        {data.lastMessageStatus === MessageStatus.Deleted
                            ? t('InboxPage.messageDeleted')
                            : data.lastMessageType === MessageType.Note
                              ? t('InboxPage.noteSnippet', { text: data.lastMessage })
                              : lastMessage[data.lastMessageContentType]}
                    </Typography>
                </div>
            </div>
            <div
                style={{
                    marginTop: data.tags?.length ? '8px' : 0,
                    display: 'grid',
                    gridTemplateColumns:
                        assignedUser || data?.assignedAgentId ? 'calc(100% - 28px) 20px' : 'calc(100%) 0px',
                    gridGap: '8px',
                    alignItems: 'center',
                }}
            >
                <div style={{ overflow: 'hidden', display: 'flex', gap: '4px' }}>
                    {data.tags?.length
                        ? data.tags.map(tag => <TagChip key={tag.id} name={tag.name} color={tag.color} />)
                        : null}
                </div>
                {assignedUser?.isRemoved && !data?.assignedAgentId ? (
                    <RemovedPersonAvatar size="xSmall" sx={{ border: 'none' }} />
                ) : (
                    (assignedUser || data?.assignedAgentId) && (
                        <StyledAvatar
                            size="xSmall"
                            fullName={`${assignedUser?.firstName} ${assignedUser?.lastName}`}
                            label={
                                data?.assignedAgentId
                                    ? null
                                    : getInitialsFull(`${assignedUser?.firstName} ${assignedUser?.lastName}`)
                            }
                            image={assignedUser?.profileImageUrl}
                            icon={data?.assignedAgentId ? <PulseAiIcon /> : <StyledUserIcon />}
                        />
                    )
                )}
            </div>
        </DialogWrapper>
    );
};

export default Dialog;
